import React, { Fragment, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Action, DetailPanel, Column } from "material-table";
import { MentorLocalizations } from "../../../Shared/sharedTypes";
import { ClientCompletedSessionStub } from "../../../Shared/GeneratedTypes";
import MaterialTable from "../MaterialTable/MaterialTable";
import {
    Avatar,
    Menu,
    MenuItem,
    Divider,
    ListItemIcon,
    ListItemText,
    SvgIconProps,
    Popover,
    Theme,
    createStyles,
    Typography,
    Button,
} from "@material-ui/core";
import moment from "moment";
import Util from "../../../Shared/utilties";
import { AssignmentInd, PlayCircleFilled, ArtTrack, VisibilityOff, Visibility } from "@material-ui/icons";
import MenuIcon from "@material-ui/icons/Menu";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-around",
            overflow: "hidden",
            //backgroundColor: theme.palette.background.paper
        },
        gridList: {
            // flexWrap: 'nowrap'
        },
        popover: {
            pointerEvents: "none",
        },
        paper: {
            padding: theme.spacing(1),
        },
        large: {
            width: theme.spacing(40),
            height: theme.spacing(40),
        },
    })
);

interface propTypes {
    forwardedRef?: any;
    assignedSessions: ClientCompletedSessionStub[];
    localizations: MentorLocalizations;
    refreshTimer: number;
    refreshNow: () => void;
    setActive: (session: ClientCompletedSessionStub) => void;
    cultureCode: string;
}

export default function QueueTable({
    assignedSessions,
    setActive,
    localizations,
    refreshTimer,
    refreshNow,
    cultureCode,
}: propTypes) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [anchorPopOverEl, setPopOverAnchorEl] = React.useState<HTMLElement | null>(null);
    const [hoveredSession, setHoveredSession] = React.useState<ClientCompletedSessionStub | null>(null);

    const handlePopoverOpen = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        session: ClientCompletedSessionStub
    ) => {
        if (session.policyHolderImage) {
            setHoveredSession(session);
            setPopOverAnchorEl(event.currentTarget);
        }
    };

    const handlePopoverClose = () => {
        setPopOverAnchorEl(null);
    };

    const [tableCols] = useState<Column<ClientCompletedSessionStub>[]>([
        {
            title: localizations.client,
            grouping: false,
            customFilterAndSearch: (filter, rowData, columDef) =>
                `${rowData.company + " " || ""} ${rowData.policyHolderFirstName} ${rowData.policyHolderLastName}`
                    .toLowerCase()
                    .indexOf(filter.toString().toLowerCase()) > -1,
            render: (rowData) => (
                <>
                    <Avatar
                        src={rowData.policyHolderImage}
                        variant="rounded"
                        style={{ display: "inline-flex", verticalAlign: "middle", marginRight: 10 }}
                        onMouseEnter={(evt) => handlePopoverOpen(evt, rowData)}
                        onMouseLeave={handlePopoverClose}
                    ></Avatar>
                    {`${rowData.company ? rowData.company + ": " : ""} ${rowData.policyHolderFirstName} ${
                        rowData.policyHolderLastName
                    }`}
                </>
            ),
        },
        { title: localizations.surveyType, field: "surveyType", grouping: true },
        {
            title: localizations.state,
            render: (rowData) => {
                if (rowData.clientSessionCompletedDate) {
                    return localizations.waitingSignOff;
                }
                if (rowData.startedDate) {
                    return localizations.sessionStarted;
                }
                if (rowData.enqueuedDate) {
                    return localizations.queued;
                }
                if (rowData.scheduledDate) {
                    return localizations.scheduled;
                }
                if (rowData.scheduledDate) {
                    return localizations.notScheduled;
                }
                return localizations.new;
            },
        },
        {
            title: localizations.scheduled,
            defaultSort: "asc",
            field: "scheduledDate",

            type: "datetime",
            render: (rowData) => {
                let momnt = moment(rowData.scheduledDate);
                return <span>{momnt.calendar()}</span>;
            },
        },
        {
            title: localizations.due,
            field: "dueDate",
            type: "datetime",
            render: (rowData) => <span>{moment(rowData.dueDate).calendar()}</span>,
        },
    ]);

    const [currentlySelected, setCurrentlySelected] = React.useState<null | ClientCompletedSessionStub>(null);

    function selectOnGuideStream() {
        if (currentlySelected) {
            setAnchorEl(null);
            setActive(currentlySelected);
        }
    }

    const handleClick = (guideStream: ClientCompletedSessionStub, event: React.MouseEvent<HTMLElement>) => {
        setCurrentlySelected(guideStream);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const popOverOpen = Boolean(anchorPopOverEl);

    let detailPanels = [
        (rowData: ClientCompletedSessionStub) => {
            return {
                icon: () => <Visibility />,
                openIcon: () => <VisibilityOff />,
                disabled: !rowData.itemDetailsLink,
                tooltip: localizations.showDetails,
                render: () => (
                    <iframe
                        title={localizations.detailsFor + " " + rowData.id}
                        width="100%"
                        height="315"
                        src={rowData.itemDetailsLink}
                    />
                ),
            } as DetailPanel<ClientCompletedSessionStub>;
        },
    ];

    let hoveredImage = hoveredSession !== null ? hoveredSession.policyHolderImage ?? undefined : undefined;

    /**********************************************
     *
     *  Please note that material table currently mutates the state (bad)
     *  Hopefully they fix that soon, for now we clone items
     *
     * https://github.com/mbrn/material-table/issues/666 <--that's never good ;)
     * https://github.com/mbrn/material-table/issues/1777
     * https://github.com/mbrn/material-table/issues/1371
     *
     *********************************************/

    return (
        <>
            <MaterialTable
                title={
                    <Typography variant="h6" style={{ whiteSpace: "nowrap" }}>
                        <AssignmentInd fontSize="inherit" /> {localizations.AssignedToMe} |{" "}
                        <Button variant="text" size="small" onClick={refreshNow}>
                            {Util.format(localizations.refreshIn, { refreshTime: refreshTimer })}
                        </Button>
                    </Typography>
                }
                localizations={localizations}
                detailPanel={detailPanels}
                options={{
                    grouping: false,
                    draggable: false,
                    search: true,
                    pageSize: 10,
                    padding: "dense",
                }}
                columns={tableCols}
                actions={[
                    {
                        icon: () => (<MenuIcon />) as React.ReactElement<SvgIconProps>,
                        tooltip: localizations.actionMenu,
                        onClick: (event, data: ClientCompletedSessionStub) => handleClick(data, event),
                    } as Action<ClientCompletedSessionStub>,
                ]}
                data={assignedSessions.map((o) => ({ ...o }))}
            />
            <Popover
                id="mouse-over-popover"
                className={classes.popover}
                classes={{
                    paper: classes.paper,
                }}
                open={popOverOpen}
                anchorEl={anchorPopOverEl}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
            >
                <Avatar src={hoveredImage} className={classes.large} variant="rounded"></Avatar>
            </Popover>
            <Menu id="tile-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
                {currentlySelected?.itemLaunchLink && (
                    <Fragment>
                        <MenuItem
                            onClick={() => {
                                window.location.href = currentlySelected?.itemLaunchLink ?? "";
                            }}
                        >
                            <ListItemIcon>
                                <ArtTrack />
                            </ListItemIcon>
                            <ListItemText>{localizations.viewRelatedItem}</ListItemText>
                        </MenuItem>
                        <Divider />
                    </Fragment>
                )}

                <MenuItem
                    onClick={() => {
                        selectOnGuideStream();
                    }}
                >
                    <ListItemIcon>
                        <PlayCircleFilled />
                    </ListItemIcon>
                    <ListItemText>{localizations.beginSession}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
